<template>
    <v-app>
        <breadcrumb :items="breadcrumbs"></breadcrumb>
        <v-container fluid>
            <div class="d-flex mb-4">
                <h2>{{ $t("esgPlatform.esgReport.createEsgTemplate") }}</h2>
                <v-spacer></v-spacer>
                <v-btn color="primary" outlined class="mr-2 btn-outline-primary font-weight-bold mb-2"
                    @click="handlerouteback">
                    {{ $t('esgPlatform.esgReport.close') }}
                </v-btn>
                <v-btn color="primary" dark class="mr-2 mb-2" @click="showTemplateSettings = true">
                    {{ $t('esgPlatform.esgReport.templateSettings') }}
                </v-btn>
                <div v-if="step === 1" class="d-flex align-center">
                    <v-btn color="primary" dark class="mr-2 mb-2" @click="step = 2">
                        {{ $t('esgPlatform.esgReport.next') }}
                    </v-btn>
                </div>
                <div v-else-if="step === 2" class="d-flex align-center">
                    <v-btn color="primary" dark class="mr-2 mb-2" @click="showSaveConfirmation = true">
                        {{ $t('esgPlatform.esgReport.save') }}
                    </v-btn>
                </div>
            </div>

            <v-card>
                <v-card-text>
                    <!-- Replace v-stepper with custom stepper -->
                    <CustomStepper :current-step="step" :steps="stepperSteps" @step-changed="handleStepChange"
                        class="mb-4" />

                    <div v-if="step === 1" class="mb-9">
                        <v-card flat>
                            <v-card-text>
                                <label for="templateName">{{ $t('esgPlatform.esgReport.templateName') }}</label>
                                <div class="my-4">
                                    <v-text-field 
                                        outlined
                                        :placeholder="$t('esgPlatform.esgReport.nameOfTemplate')"
                                        dense 
                                        class="shrink" 
                                        v-model="templateName"
                                    ></v-text-field>
                                </div>
                                <div>
                                    <!-- Added :total-sections prop -->
                                    <SectionItem v-for="(section, idx) in sections" :key="section._id || idx" :section="section"
                                        :index="idx" @delete-section="deleteSection"
                                        :total-sections="sections.length"  
                                        @move-section-up="moveSectionUp"
                                        @move-section-down="moveSectionDown" 
                                        @reorder-blocks="handleReorderBlocks" 
                                    />
                                    <AddNewButton color="green" @click="addSection()">{{ $t('esgPlatform.esgReport.addNewSection') }}
                                    </AddNewButton>
                                </div>
                            </v-card-text>
                            <v-card-actions>
                                <v-btn color="primary" @click="step = 1">{{ $t('esgPlatform.esgReport.back') }}</v-btn>
                                <v-btn color="primary" @click="step = 2">{{ $t('esgPlatform.esgReport.next') }}</v-btn>
                            </v-card-actions>
                        </v-card>
                    </div>
                    
                    <div v-else-if="step === 2">
                        <v-card flat>
                            <v-card-text>
                                <h3>{{ $t("esgPlatform.esgReport.previewReport") }}</h3>
                                <p>{{ $t("esgPlatform.esgReport.reportPreviewMessage") }}</p>
                            </v-card-text>
                            <PreviewSection :sections="sections" @delete-section="deleteSection" />
                            <v-card-actions>
                                <v-btn color="primary" @click="step = 1">{{ $t('esgPlatform.esgReport.back') }}</v-btn>
                                <v-btn color="primary" @click="showSaveConfirmation = true">{{ $t('esgPlatform.esgReport.save') }}</v-btn>
                            </v-card-actions>
                        </v-card>
                    </div>
                </v-card-text>
            </v-card>
        </v-container>


        <TemplateSettingsDialog v-model="showTemplateSettings" :settings="templateSettings"
            @save="templateSettings = $event" />
        <ConfirmationDialog :visible="showSaveConfirmation" title="Confirmation"
            message="Are you sure you want to save your changes?" confirm-text="CONFIRM" cancel-text="CANCEL"
            type="warning" @cancel="showSaveConfirmation = false" @close="showSaveConfirmation = false"
            @confirm="handleSaveChanges" />

        <!-- Add this snackbar component before closing </v-app> -->
        <v-snackbar
            v-model="snackbar.show"
            :color="snackbar.color"
            :timeout="4000"
            bottom
            right
        >
            {{ snackbar.text }}
            <template v-slot:action="{ attrs }">
                <v-btn
                    text
                    v-bind="attrs"
                    @click="snackbar.show = false"
                >
                    Close
                </v-btn>
            </template>
        </v-snackbar>
    </v-app>
</template>

<script>
import SectionItem from './components/step2/SectionItem.vue';
import AddNewButton from './components/step2/AddNewButton.vue';
import TemplateSettingsDialog from './components/TemplateSettingDialog.vue';
import PreviewSection from './components/step3/PreviewSection.vue';
import ConfirmationDialog from '../components/Confirmation.vue';
import CustomStepper from '../components/CustomStepperTab.vue';
import EsgService from '@/_services/EsgService';
import LoadingMixin from "@/mixins/LoadingMixin";

export default {
    name: 'CreateReport',
    components: {
        SectionItem,
        AddNewButton,
        TemplateSettingsDialog,
        PreviewSection,
        ConfirmationDialog,
        CustomStepper, // Add this component
    },
    computed: {
        reportSections() {
            return this.sections.map(section => ({
                ...section,
                settings: {
                    ...section.settings,
                    backgroundColor: section.settings.backgroundColor || '#ffffff',
                    fontColor: section.settings.fontColor || '#000000',
                    font: section.settings.font || 'Poppins',
                    timeline: section.settings.timeline || '2026 - 2028',
                    locked: section.settings.locked || false
                }
            }));
        },
        getOrganizationId() {
            return this.$store.getters.getUser?.user_organization?.id;
        },
    },
    async created() {
        this.templateId = this.$route.query.templateId;

        if (this.templateId) {
            const response = await EsgService.getEsgReportTemplateById(this.templateId);

            if (!response.success) {
                this.$router.back();
                return;
            }

            this.templateName = response.data.templateName;
            this.templateSettings =  response.data.templateSettings;
            this.sections = response.data.sections;

            if (this.$route.query.preview === '1') {
                this.step = 2;
            }
        }
    },
    data() {
        return {
            templateId: null,
            step: 1,
            timeline: ['2026', '2027'], // Changed from string to array for multi-select
            breadcrumbs: [
                { text: this.$t('esgPlatform.esgReport.esgPlatform') , to: '/esg-platform/esg-dashboard' },
                { text: this.$t('esgPlatform.esgReport.reportBuilder'), to: '/esg-platform/esg-reports-builder' },
                { text: this.$t('esgPlatform.esgReport.manageTemplates'), to: '/esg-platform/manage-template' },
                { text: this.$t('esgPlatform.esgReport.createTemplate'), active: true },
            ],
            selectedTemplate: null,
            sections: [],
            templateName: '',
            dialog: false,
            selectedSection: null,
            showTemplateSettings: false,
            templateSettings: {
                bgColor: '#ffffff',
                fontColor: '#101010',
                font: 'Poppins',
                pdfHeaderBg: '#244513',
                pdfHeaderFontColor: '#1A5685',
                pdfHeaderFont: 'Poppins',
                logo: ''
            },
            showDialog: false,
            showPublishConfirmation: false,
            sectionsGetByIndex: [],
            preview: false,
            showDownloadMenu: false,
            showSaveConfirmation: false,
            stepperSteps: [
                { title: this.$t('esgPlatform.esgReport.createTemplate'), tooltip: this.$t('esgPlatform.esgReport.tooltipCreateTemplate') },
                { title: this.$t('esgPlatform.esgReport.preview'), tooltip: this.$t('esgPlatform.esgReport.tooltipPreview') }
            ],
            timelineOptions: [
                '2026',
                '2027',
                '2028',
                '2029',
                '2030',
                '2031',
                '2032'
            ],
            snackbar: {
                show: false,
                text: '',
                color: 'success'
            },
        };
    },
    methods: {
        async handleSaveChanges() {
            // Validate input
            if (this.templateName.length === 0 || this.sections.length === 0) {
                this.$notify({
                    type: "error",
                    text: "Empty template cannot be saved",
                });
                return;
            }

            const templatePayload = {
                templateName: this.templateName,
                templateSettings: this.templateSettings,
                sections: this.sections
            };

            this.startLoading();
            try {
                // Determine if updating or creating
                const response = this.templateId
                    ? await EsgService.updateEsgReportTemplate(this.templateId, templatePayload)
                    : await EsgService.createEsgReportTemplates(templatePayload);
                
                if (response.success) {
                    this.$notify({
                        type: "success",
                        text: "Template saved successfully",
                    });
                    this.handlerouteback();
                } else {
                    this.$notify({
                        type: "error",
                        text: response.message || "Failed to save template",
                    });
                    this.showSaveConfirmation = false;
                }
            } catch (error) {
                console.error('Failed to save template:', error);
                this.$notify({
                    type: "error",
                    text: "An unexpected error occurred. Please try again.",
                });
                this.showSaveConfirmation = false;
            } finally {
                this.stopLoading();
            }
        },
        openEditor(section = null) {
            this.selectedSection = section;
            this.dialog = true;
        },
        addSection() {
            const next = this.sections.length + 1;
            this.sections.push({
            title: this.$t('esgPlatform.esgReport.section')+ ` ${next}`, 
                headingSize: 'H1',
                settings: {
                    backgroundColor: '#ffffff',
                    fontColor: '#000000',
                    font: 'Poppins',
                    timeline: '2026 - 2028',
                    locked: false
                },
                blocks: []
            });
        },
        deleteSection(index) {
            this.sections.splice(index, 1);
        },
        handleStepChange(newStep) {
            this.step = newStep;
        },
        showSuccess(message) {
            this.snackbar = {
                show: true,
                text: message,
                color: 'success'
            };
        },
        showError(message) {
            this.snackbar = {
                show: true,
                text: message,
                color: 'error'
            };
        },
        resetPayloadData() {
            this.timeline = []; // Reset to empty array
            this.selectedTemplate = null;
            this.sections = [];
        },
        handlerouteback() {
            const fromPage = this.$route.query.fromPage;
            if (fromPage === 'manageTemplate') {
                this.$router.push({ name: 'manageTemplate' });
            } else if (fromPage === 'CreateNewStartTemplate') {
                this.$router.push({ name: 'createNewEsgReport' });
            } else {
                this.$router.push({ name: 'manageTemplate' });
            }
        },
        moveSectionUp(index) {
        if (index <= 0) return;
        
        const temp = this.sections[index];
        this.$set(this.sections, index, this.sections[index - 1]);
        this.$set(this.sections, index - 1, temp);
        const changedSections = [
            { _id: this.sections[index - 1]?._id, order: index - 1 },
            { _id: this.sections[index]?._id, order: index }
        ]. filter(s => s._id);
        this.saveReorder({ sections: changedSections });
        },

        moveSectionDown(index) {
        if (index >= this.sections.length - 1) return;
        
        const temp = this.sections[index];
        this.$set(this.sections, index, this.sections[index + 1]);
        this.$set(this.sections, index + 1, temp);
                const changedSections = [
            { _id: this.sections[index]?._id, order: index },
            { _id: this.sections[index + 1]?._id, order: index + 1 }
        ].filter(s => s._id);
        
        this.saveReorder({ sections: changedSections });
        },

        handleReorderBlocks({ sectionId, swappedBlocks }) {
        if (!sectionId || !swappedBlocks || swappedBlocks.length === 0) return;
        
        const changedBlocks = swappedBlocks
            .filter(b => b._id)
            .map(b => ({
            _id: b._id,
            sectionId: sectionId,
            order: b.order
            }));
        this.saveReorder({ blocks: changedBlocks });
        },
        async saveReorder(changedData = {}) {
        if (!this.templateId) return;
        
        try {
            const payload = {};
            if (changedData.sections && changedData.sections.length > 0) {
                payload.sections = changedData.sections;
            }
            if (changedData.blocks && changedData.blocks.length > 0) {
                payload.blocks = changedData.blocks;
            }
            if (Object.keys(payload).length > 0) {
                await EsgService.reOrderEsgReportTemplate(this.templateId, payload);
            }
        } catch (error) {
            console.error('Failed to save reorder:', error);
            this.showError('Failed to save order. Please try again.');
            }
        },
    },
      mixins: [ LoadingMixin ],
};
</script>

<style scoped>
.v-stepper {
    box-shadow: none !important;
}

.v-card__text {
    padding: 6px !important;
}

.v-stepper__content {
    padding: 0px !important;
}
</style>